// Tiny fetch wrapper. Centralizes API base URL + auth + JSON parsing
// + auth-error redirect to /login.

import { authHeaders, clearToken } from "./auth";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";
if (!BASE) {
  // Fail loud at startup so we don't ship a broken build silently.
  console.error(
    "[dashboard] VITE_API_BASE_URL not set at build time — set it to the Worker URL",
  );
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  });
  if (resp.status === 401) {
    clearToken();
    if (window.location.pathname !== "/login") window.location.href = "/login";
    throw new ApiError(401, "unauthorized");
  }
  if (!resp.ok) {
    let body: { error?: string } = {};
    try { body = await resp.json(); } catch { /* non-json body */ }
    throw new ApiError(resp.status, body.error ?? `${resp.status} ${resp.statusText}`);
  }
  return (await resp.json()) as T;
}

// --- typed endpoints ----------------------------------------------------

export interface AuditSummary {
  ranAt: string;
  ok: boolean;
  passCount: number;
  warnCount: number;
  errorCount: number;
}

export interface SiteRow {
  id: string;
  domain: string;
  city: string | null;
  state: string | null;
  niche: string | null;
  templateVariant: string | null;
  mode: string;
  /** site_kind: internal_lead_gen (default) | external_managed (added 2026-06-02). */
  siteKind?: string;
  status: string;
  hostingProvider: string | null;
  generatedAt: string | null;
  deployedAt: string | null;
  /** Per-site author name from sites.author_name. null = falls back to operator.yaml. */
  authorName: string | null;
  /** Count of rows in blog_posts for this site. 0 = "Articles coming soon" stub renders. */
  blogPostCount: number;
  totalLeads: number;
  recentLeads: number; // last 7 days
  highScoreLeads: number; // score >= 50
  latestAudit: AuditSummary | null;
  /** URLs covered = "indexed" per SC URL Inspection. Sourced from page_index_status. */
  indexedUrls: number;
  /** Total URLs we've inspected (denominator for indexed%). */
  totalUrlsChecked: number;
  /** Legacy combined count (mixes desktop + mobile + GSC). Directional. */
  rankedKeywords: number;
  /** Split per source — added 2026-05-15 with the dashboard trust hardening. */
  rankings: {
    /** device='desktop' rank-pull rows, position ≤ 100. */
    desktop: number;
    /** device='mobile' rank-pull rows, position ≤ 100. */
    mobile: number;
    /** Distinct GSC queries with impressions > 0 over last 30d. */
    gscQueriesSeen: number;
  };
  /** Per-site Twilio/SignalWire tracking number, or null if not provisioned. */
  phoneNumber: string | null;
  /** voicemail | forward | hybrid | null. */
  callMode: string | null;
}

export interface AuditCheck {
  category: string;
  name: string;
  severity: "pass" | "warn" | "error";
  message: string;
  page: string | null;
}

export interface AuditDetail extends AuditSummary {
  id: string;
  checks: AuditCheck[];
}

export interface LinkVelocity {
  /** Current internal link count from the latest audit. */
  current: number;
  /** Change vs the most recent audit ≥ 7 days old (null = no baseline). */
  deltaWeek: number | null;
  /** Pages crawled (for avg-per-page calc). null = pre-feature run. */
  pageCount: number | null;
}

export interface AuditResponse {
  latest: AuditDetail | null;
  previousOk: boolean | null;
  runCount: number;
  /** Internal-link velocity snapshot. null when latest audit is from
   *  before this feature shipped (DB column was added 2026-05-05). */
  linkVelocity: LinkVelocity | null;
}

export interface Client {
  id: string;
  businessName: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
  createdAt: string;
}
export interface ClientsResponse { clients: Client[]; }
export interface ClientInput {
  businessName: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  notes?: string;
}

export interface Rental {
  id: string;
  siteId: string;
  siteDomain: string | null;
  clientId: string;
  clientName: string | null;
  monthlyRate: number;
  status: string;
  startDate: string | null;
  endDate: string | null;
  paymentMethod: string | null;
  notes: string | null;
}
export interface RentalsResponse { rentals: Rental[]; }
export interface RentalInput {
  siteId: string;
  clientId: string;
  monthlyRate: number;
  status?: string;
  startDate?: string;
  paymentMethod?: string;
  notes?: string;
}

export interface LeadRow {
  id: string;
  siteId: string;
  siteDomain: string | null;
  type: string;
  callerName: string | null;
  callerPhone: string | null;
  callerEmail: string | null;
  message: string | null;
  isSpam: boolean;
  qualityScore: number | null;
  receivedAt: string;
}

export interface SiteDetail {
  id: string;
  domain: string;
  city: string | null;
  state: string | null;
  niche: string | null;
  primaryKeyword: string | null;
  templateVariant: string | null;
  mode: string;
  /** site_kind: internal_lead_gen (default) | external_managed. */
  siteKind?: string;
  /** External publish endpoint URL (external_managed only). */
  externalPublishUrl?: string | null;
  /** Whether the external_publish_secret is set (true/false; secret itself never returned). */
  externalPublishSecretSet?: boolean;
  /** Default DataForSEO probe location (external_managed only). */
  defaultProbeLocation?: string | null;
  status: string;
  hostingProvider: string | null;
  hostingAccount: string | null;
  hostingProjectId: string | null;
  phoneNumber: string | null;
  phoneForwardTo: string | null;
  ga4PropertyId: string | null;
  ga4MeasurementId: string | null;
  searchConsoleVerified: boolean;
  generatedAt: string | null;
  deployedAt: string | null;
  monthlyCost: number | null;
  contentHash: string | null;
  notes: string | null;
  /** Phase B2 indexing tier: 1 = full, 2 = home+services only, 3 = noindex,follow. */
  tier: 1 | 2 | 3;
  /** Per-site author persona (null = falls back to operator.yaml's author). */
  authorName: string | null;
  /** Blog post count from blog_posts table. */
  blogPostCount: number;
  authorSlug: string | null;
  authorTitle: string | null;
  authorBio: string | null;
  totalLeads: number;
  recentLeads: number;
  highScoreLeads: number;
  spamLeads: number;
  lastLeadAt: string | null;
}

export interface BlogPostRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  keyword: string | null;
  datePublished: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface BlogPostsResponse { posts: BlogPostRow[]; }

export interface BlogPublishEventRow {
  id: string;
  postSlug: string | null;
  action: string;
  detailsJson: Record<string, unknown> | null;
  occurredAt: string;
}
export interface BlogPublishEventsResponse { events: BlogPublishEventRow[]; }

// Trust label contract — must match server's TrustLabel.
export type TrustLevel = "verified" | "directional" | "contaminated" | "blocked";
export interface TrustLabel { level: TrustLevel; reason?: string; caveat?: string }
export type DataIntegrity = Record<string, TrustLabel>;

export interface SitesResponse { sites: SiteRow[]; dataIntegrity?: DataIntegrity }
export interface SiteDetailResponse {
  site: SiteDetail;
  recentLeads: LeadRow[];
  showTests?: boolean;
  dataIntegrity?: DataIntegrity;
}
export interface LeadsResponse {
  leads: LeadRow[];
  nextCursor: string | null;
  showTests?: boolean;
  includeArchived?: boolean;
  dataIntegrity?: DataIntegrity;
}

export interface ScTotals {
  impressions: number;
  clicks: number;
  avgPosition: number | null;
  keywords: number;
  firstDate: string | null;
  lastDate: string | null;
}
export interface ScDaily { day: string; impressions: number; clicks: number; avgPosition: number | null; }
export interface ScQuery { keyword: string; impressions: number; clicks: number; avgPosition: number | null; }
export interface ScResponse { days: number; totals: ScTotals; daily: ScDaily[]; topQueries: ScQuery[]; dataRefreshedAt: string | null; }

export interface Ga4Totals { totalUsers: number; newUsers: number; sessions: number; engagedSessions: number; }
export interface Ga4Daily {
  day: string; totalUsers: number; newUsers: number; sessions: number; engagedSessions: number; avgSessionDuration: number;
}
export interface Ga4TopPage { pagePath: string; sessions: number; users: number; }
export interface Ga4Response { days: number; totals: Ga4Totals; daily: Ga4Daily[]; topPages: Ga4TopPage[]; dataRefreshedAt: string | null; }

export interface PressRelease {
  id: string;
  siteId: string;
  type: string;
  headline: string;
  body: string;
  status: string;
  distributedTo: string | null;
  notes: string | null;
  createdAt: string;
  submittedAt: string | null;
}
export interface PressReleasesResponse { pressReleases: PressRelease[]; }
export interface DraftPrInput { type: "launch" | "service-expansion" | "milestone"; neighborhood?: string; milestoneCount?: number; }
export interface DraftPrResponse { ok: boolean; id: string; headline: string; body: string; }

export interface JournoQuery {
  id: string;
  title: string;
  bodySnippet: string | null;
  url: string;
  postedAt: string;
  score: number;
  matched: string[] | null;
  status: string;
  notes: string | null;
  pitchedAt: string | null;
  landedAt: string | null;
  landedUrl: string | null;
  pulledAt: string;
}
export interface JournoQueriesResponse { queries: JournoQuery[]; }

export interface Outreach {
  id: string;
  siteId: string | null;
  type: string;
  target: string;
  targetUrl: string | null;
  status: string;
  cost: number | null;
  linkUrl: string | null;
  notes: string | null;
  createdAt: string;
  contactedAt: string | null;
  placedAt: string | null;
}
export interface OutreachResponse { outreach: Outreach[]; }
export interface OutreachInput {
  siteId?: string;
  type: string;
  target: string;
  targetUrl?: string;
  cost?: number;
  notes?: string;
}

export interface AuditHistoryRun {
  id: string;
  ranAt: string;
  ok: boolean;
  passCount: number;
  warnCount: number;
  errorCount: number;
}
export interface AuditHistoryResponse { runs: AuditHistoryRun[]; }

export interface SiteFreshness {
  id: string;
  domain: string;
  lastAuditAt: string | null;
  lastLeadAt: string | null;
  lastScPullAt: string | null;
  lastGa4PullAt: string | null;
}
export interface IndexingStats {
  total: number;
  indexed: number;
  notIndexed: number;
  excluded: number;
  other: number;
  mobilePass: number;
  withRichResults: number;
}
export interface IndexingPage {
  id: string;
  url: string;
  verdict: string | null;
  coverageState: string | null;
  robotsTxtState: string | null;
  indexingState: string | null;
  pageFetchState: string | null;
  lastCrawled: string | null;
  googleCanonical: string | null;
  userCanonical: string | null;
  mobileUsabilityVerdict: string | null;
  richResultsTypes: string[] | null;
  lastChecked: string;
}
export interface IndexingResponse { stats: IndexingStats; pages: IndexingPage[]; dataRefreshedAt: string | null; }

export interface KeywordHistoryPoint { day: string; position: number | null; impressions: number; clicks: number; }
export interface KeywordHistoryResponse { days: number; keywords: Record<string, KeywordHistoryPoint[]>; }

export interface KeywordSummary {
  keyword: string;
  impressions: number;
  clicks: number;
  avgPosition: number | null;
  recentPosition: number | null;
  /** Positive = improving (lower position number is better). */
  delta: number | null;
  isTarget: boolean;
}
export interface KeywordsResponse {
  days: number;
  targetCount: number;
  targetRankedCount: number;
  targets: KeywordSummary[];
  surprises: KeywordSummary[];
}

export interface CommandCenterHeadline {
  siteCount: number;
  liveSiteCount: number;
  totalLeads7d: number;
  highScoreLeads7d: number;
  totalImpressions7d: number;
  totalClicks7d: number;
  sitesWithAuditFailures: number;
  sitesIndexedPartial: number;
  sitesNeverAudited: number;
  newJournoCount: number;
  outreachPlacedCount: number;
  prDraftsPending: number;
}
export interface CommandCenterTopSite { id: string; domain: string; recentLeads?: number; impressions?: number; }
export interface CommandCenterResponse {
  headline: CommandCenterHeadline;
  topSitesByLeads: CommandCenterTopSite[];
  topSitesByImpressions: CommandCenterTopSite[];
}

export interface FreshnessResponse {
  lastJournoPullAt: string | null;
  sites: SiteFreshness[];
}

// ─── Agents ────────────────────────────────────────────────────────────

export type AgentTrustPhase = "dry_run" | "confirm" | "autonomous";

export interface AgentSummary {
  id: string;
  displayName: string;
  role: string;
  version: string;
  enabled: boolean;
  trustPhase: AgentTrustPhase;
  hourlyBudget: number;
  dailyBudget: number;
  intervalSec: number;
  emoji: string | null;
  avatarUrl: string | null;
  lastRunAt: string | null;
  nextRunAt: string | null;
  stats: {
    actionsLastHour: number;
    actionsLast24h: number;
    ok24h: number;
    failed24h: number;
    spendCents30d: number;
  };
}
export interface AgentsListResponse { agents: AgentSummary[]; }

export interface AgentAction {
  id: string;
  actionKind: string;
  siteId: string | null;
  alertId: string | null;
  outcome: string;
  errorMessage: string | null;
  inputState: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  startedAt: string;
  endedAt: string | null;
  dryRun: boolean;
}
export interface AgentDetailResponse {
  agent: AgentSummary & { runbookPath: string | null };
  actions: AgentAction[];
  perSite: Array<{
    siteId: string | null;
    domain: string | null;
    actionCount: number;
    okCount: number;
    failedCount: number;
    lastAt: string | null;
  }>;
}

export interface SiteAgentAction {
  id: string;
  agentId: string;
  agentDisplayName: string;
  agentEmoji: string | null;
  actionKind: string;
  outcome: string;
  startedAt: string;
  dryRun: boolean;
  errorMessage: string | null;
}
export interface SiteAgentActionsResponse { actions: SiteAgentAction[]; }

export interface RecentAgentAction {
  id: string;
  agentId: string;
  agentDisplayName: string;
  agentEmoji: string | null;
  siteId: string | null;
  domain: string | null;
  alertId: string | null;
  actionKind: string;
  outcome: string;
  errorMessage: string | null;
  startedAt: string;
  dryRun: boolean;
}
export interface RecentAgentActionsResponse { actions: RecentAgentAction[]; }

// ─── Confirm-phase queue ──────────────────────────────────────────────

export type AgentActionIntent =
  | {
      kind: "raise-alert";
      payload: {
        dedupKey: string;
        siteId?: string | null;
        severity: "info" | "warn" | "critical";
        category: string;
        title: string;
        description?: string | null;
        /** When set, the raised alert gets an Execute button. Owen's
         *  brief intents include `targetUrl` here so the pending-
         *  approval card can show what page the action will modify. */
        executable?: { kind: string; targetUrl?: string; [k: string]: unknown };
        threadKey?: string | null;
      };
    }
  | { kind: "auto-resolve-alert"; dedupKey: string; note?: string }
  | {
      kind: "regenerate-with-brief";
      payload: {
        siteId: string;
        domain: string;
        keyword: string;
        briefAgentActionId: string;
      };
    }
  | {
      kind: "add-internal-link";
      payload: {
        siteId: string;
        domain: string;
        blogPostId: string;
        anchorText: string;
        targetUrl: string;
      };
    };

export interface PendingAgentAction {
  id: string;
  agentId: string;
  agentDisplayName: string;
  agentEmoji: string | null;
  actionKind: string;
  siteId: string | null;
  siteDomain: string | null;
  inputState: Record<string, unknown> | null;
  intent: AgentActionIntent | null;
  proposedAt: string;
}

export interface PendingActionsResponse {
  pending: PendingAgentAction[];
}

export interface DecisionResponse {
  ok: boolean;
  decision: "approved" | "rejected";
  execOutcome?: "ok" | "failed";
  error?: string;
}

export type PipelineStageKind = "agent" | "queue" | "alerts" | "manual" | "view";
export interface PipelineStageLive {
  id: string;
  kind: PipelineStageKind;
  label: string;
  description: string | null;
  linkTo: string | null;
  countLabel: string | null;
  count: number | null;
  // Only present for kind=agent.
  agentId?: string;
  agentEnabled?: boolean | null;
  agentTrustPhase?: string | null;
  agentVersion?: string | null;
  lastRunAt?: string | null;
  nextRunAt?: string | null;
  intervalSec?: number | null;
}
export interface PipelineLive {
  id: string;
  name: string;
  description: string;
  stages: PipelineStageLive[];
}
export interface PipelinesResponse { pipelines: PipelineLive[]; }
export const pipelinesApi = {
  list: () => api<PipelinesResponse>("/api/dashboard/pipelines"),
};

export const agentsApi = {
  list: () => api<AgentsListResponse>("/api/dashboard/agents"),
  detail: (id: string) =>
    api<AgentDetailResponse>(`/api/dashboard/agents/${encodeURIComponent(id)}`),
  recentActions: (limit = 20) =>
    api<RecentAgentActionsResponse>(`/api/dashboard/agents/recent-actions?limit=${limit}`),
  control: (
    id: string,
    body: { action: "enable" | "disable" | "set-phase"; trustPhase?: AgentTrustPhase },
  ) =>
    api<{ ok: boolean }>(`/api/dashboard/agents/${encodeURIComponent(id)}/control`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  runNow: (id: string) =>
    api<{ ok: boolean; runsUrl?: string; error?: string }>(`/api/dashboard/agents/${encodeURIComponent(id)}/run-now`, {
      method: "POST",
    }),
  forSite: (siteId: string) =>
    api<SiteAgentActionsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/agent-actions`),
  pending: () => api<PendingActionsResponse>("/api/dashboard/agents/pending"),
  approve: (id: string) =>
    api<DecisionResponse>(`/api/dashboard/agents/pending/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    }),
  reject: (id: string) =>
    api<DecisionResponse>(`/api/dashboard/agents/pending/${encodeURIComponent(id)}/reject`, {
      method: "POST",
    }),
  activity: (id: string, windowMin = 30) =>
    api<AgentActivityResponse>(
      `/api/dashboard/agents/${encodeURIComponent(id)}/activity?window=${windowMin}`,
    ),
};

export interface AgentActivitySignal {
  label: string;
  value: string | number;
  hint?: string;
}
export interface AgentActivityKeyword {
  keyword: string;
  ourPosition: number | null;
  localPack: boolean;
  top3: string[];
  probedAt: string;
}
export interface AgentActivityPerSite {
  siteId: string;
  domain: string;
  keywordsProbed: number;
  lastWriteAt: string;
  /** Spencer-only: each keyword's most recent state for inline expansion. */
  keywords?: AgentActivityKeyword[];
}

// ─── Per-site competitive landscape (Spencer's research, persistent) ──

export type CompetitorClass = "directory" | "national_chain" | "local";

export interface SiteCompetitorTop10Entry {
  domain: string;
  classification: CompetitorClass;
  isUs: boolean;
  domainRating: number | null;
  backlinksCount: number | null;
  refDomainsCount: number | null;
}
export interface SiteCompetitorKeyword {
  keyword: string;
  locationName: string | null;
  ourPosition: number | null;
  localPack: boolean;
  localPackPosition: number | null;
  probedAt: string;
  top10: SiteCompetitorTop10Entry[];
}
export interface SiteCompetitorIncumbent {
  domain: string;
  appearances: number;
  keywordsTotal: number;
  domainRating: number | null;
  backlinksCount: number | null;
  refDomainsCount: number | null;
}
export interface SiteCompetitorMarketStructure {
  totalSlots: number;
  directorySlots: number;
  chainSlots: number;
  localSlots: number;
  distinctLocals: number;
  difficulty: number;
  difficultyLabel: "easier" | "medium" | "harder" | "saturated";
}
export interface SiteCompetitorsResponse {
  siteId: string;
  domain: string;
  lastProbedAt: string | null;
  marketStructure: SiteCompetitorMarketStructure | null;
  localIncumbents: SiteCompetitorIncumbent[];
  keywords: SiteCompetitorKeyword[];
  message?: string;
}
export interface AgentActivityResponse {
  agentId: string;
  windowMin: number;
  isLive: boolean;
  lastActivityAt: string | null;
  signals: AgentActivitySignal[];
  perSite: AgentActivityPerSite[] | null;
}

// ─── Market gaps — portfolio expansion candidates ──────────────────────

export interface MarketGap {
  id: string;
  niche: string;
  city: string;
  state: string;
  primaryKeyword: string;
  searchVolume: number | null;
  cpc: number | null;
  keywordDifficulty: number | null;
  competitionDensity: number | null;
  opportunityScore: number | null;
  scannedAt: string;
  status: string;
}
export interface MarketGapsResponse {
  gaps: MarketGap[];
  totals: { scanned: number; claimed: number; unclaimed: number };
}

export const marketsApi = {
  gaps: (params: { niche?: string; state?: string; minScore?: number; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.niche) qs.set("niche", params.niche);
    if (params.state) qs.set("state", params.state);
    if (params.minScore != null) qs.set("min_score", String(params.minScore));
    if (params.limit != null) qs.set("limit", String(params.limit));
    return api<MarketGapsResponse>(`/api/dashboard/markets/gaps${qs.size ? "?" + qs.toString() : ""}`);
  },
};

export const sitesApi = {
  list: () => api<SitesResponse>("/api/dashboard/sites"),
  detail: (id: string) => api<SiteDetailResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}`),
  sc: (id: string, days = 30) => api<ScResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/sc?days=${days}`),
  audit: (id: string) => api<AuditResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/audit`),
  ahrefsIssues: (id: string) =>
    api<AhrefsIssuesResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/ahrefs-issues`),
  ga4: (id: string, days = 30) => api<Ga4Response>(`/api/dashboard/sites/${encodeURIComponent(id)}/ga4?days=${days}`),
  indexing: (id: string) => api<IndexingResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/indexing`),
  keywordHistory: (id: string, days = 30) =>
    api<KeywordHistoryResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/keyword-history?days=${days}`),
  keywords: (id: string, days = 30) =>
    api<KeywordsResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/keywords?days=${days}`),
  setMode: (id: string, mode: "lead_gen" | "branded_lessee") =>
    api<{ ok: boolean; nextStep: string }>(`/api/dashboard/sites/${encodeURIComponent(id)}/mode`, {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  setTier: (id: string, tier: 1 | 2 | 3) =>
    api<{ ok: boolean; nextStep: string }>(`/api/dashboard/sites/${encodeURIComponent(id)}/tier`, {
      method: "POST",
      body: JSON.stringify({ tier }),
    }),
  clearAuthor: (id: string) =>
    api<{ ok: boolean; nextStep: string }>(`/api/dashboard/sites/${encodeURIComponent(id)}/author/clear`, {
      method: "POST",
    }),
  blogPosts: (id: string) =>
    api<BlogPostsResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/blog-posts`),
  blogPublishEvents: (id: string) =>
    api<BlogPublishEventsResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/blog-publish-events`),
  auditHistory: (id: string, limit = 30) =>
    api<AuditHistoryResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/audit-history?limit=${limit}`),
  pressReleases: (id: string) =>
    api<PressReleasesResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/press-releases`),
  draftPr: (id: string, input: DraftPrInput) =>
    api<DraftPrResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/pr`, {
      method: "POST", body: JSON.stringify(input),
    }),
  competitors: (id: string) =>
    api<SiteCompetitorsResponse>(`/api/dashboard/sites/${encodeURIComponent(id)}/competitors`),
};

export const pressReleasesApi = {
  update: (id: string, patch: Partial<{ status: string; distributedTo: string; notes: string; submittedAt: string }>) =>
    api<{ ok: boolean }>(`/api/dashboard/press-releases/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
};

export const journoQueriesApi = {
  list: () => api<JournoQueriesResponse>("/api/dashboard/journo-queries"),
  update: (id: string, patch: Partial<{ status: string; notes: string; landedUrl: string }>) =>
    api<{ ok: boolean }>(`/api/dashboard/journo-queries/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
};

export interface RefreshDataStatus {
  lastDispatchedAt: string | null;
  onCooldown: boolean;
  retryAfterSec: number;
  cooldownHours: number;
}

export interface RefreshDataResult {
  ok: boolean;
  dispatchedAt?: string;
  runUrl?: string;
  message?: string;
  error?: string;
  retryAfterSec?: number;
  lastDispatchedAt?: string;
}

export interface FinanceProviderSpend {
  provider: string;
  category?: string | null;
  costCents: number;
  events?: number;
}
export interface FinanceRecurringItem {
  id: string;
  provider: string;
  description: string;
  monthlyCostCents: number;
  createdAt: string;
}
export interface FinanceDailyPoint {
  day: string;
  costCents: number;
}
export interface FinanceResponse {
  monthToDate: { totalCents: number; byProviderCategory: FinanceProviderSpend[]; };
  last30Days: { totalCents: number; byProvider: FinanceProviderSpend[]; };
  recurring: { monthlyTotalCents: number; items: FinanceRecurringItem[]; };
  dailySpend: FinanceDailyPoint[];
  annualRunRateCents: number;
}
export interface RenewalsItem {
  domain: string;
  expiresAt: string | null;
  daysUntilExpiry: number | null;
  autoRenew: boolean | null;
  status: string | null;
}
export interface RenewalsResponse {
  domains: RenewalsItem[];
  nextRenewals30d: RenewalsItem[];
  autoRenewOff: RenewalsItem[];
}

export const opsApi = {
  freshness: () => api<FreshnessResponse>("/api/dashboard/ops/freshness"),
  commandCenter: () => api<CommandCenterResponse>("/api/dashboard/command-center"),
  refreshDataStatus: () => api<RefreshDataStatus>("/api/dashboard/ops/refresh-data/status"),
  refreshData: () =>
    api<RefreshDataResult>("/api/dashboard/ops/refresh-data", { method: "POST" }),
  finance: () => api<FinanceResponse>("/api/dashboard/ops/finance"),
  renewals: () => api<RenewalsResponse>("/api/dashboard/ops/finance/renewals"),
};

// ─── Content screen (added 2026-05-31) ─────────────────────────────────

export interface ContentRecentPost {
  id: string;
  slug: string;
  title: string;
  keyword: string | null;
  excerpt: string | null;
  datePublished: string | null;
  createdAt: string;
  updatedAt: string | null;
  siteId: string;
  domain: string;
  niche: string | null;
  city: string | null;
  state: string | null;
  liveUrl: string;
}

export interface ContentTopPost {
  id: string;
  slug: string;
  title: string;
  keyword: string | null;
  datePublished: string | null;
  siteId: string;
  domain: string;
  niche: string | null;
  city: string | null;
  state: string | null;
  imps30d: number;
  clicks30d: number;
  topQuery: string | null;
  liveUrl: string;
}

export interface ContentEvent {
  id: string;
  agentId: string;
  agentDisplayName: string;
  agentEmoji: string | null;
  actionKind: string;
  outcome: string;
  siteId: string | null;
  domain: string | null;
  startedAt: string;
  errorMessage: string | null;
}

export const contentApi = {
  recentPosts: (days = 30, limit = 50) =>
    api<{ posts: ContentRecentPost[] }>(`/api/dashboard/content/recent-posts?days=${days}&limit=${limit}`),
  topPosts: (days = 30, limit = 30) =>
    api<{ posts: ContentTopPost[] }>(`/api/dashboard/content/top-posts?days=${days}&limit=${limit}`),
  events: (days = 7, limit = 100) =>
    api<{ events: ContentEvent[] }>(`/api/dashboard/content/events?days=${days}&limit=${limit}`),
};

// ─── External-managed sites (added 2026-06-02 Phase 2) ────────────────

export interface ExternalKeyword {
  id: string;
  keyword: string;
  locationName: string | null;
  tag: string | null;
  addedAt: string;
}
export interface ExternalCompetitor {
  id: string;
  competitorDomain: string;
  notes: string | null;
  addedAt: string;
}
export interface ExternalPushHistoryItem {
  id: string;
  slug: string;
  title: string;
  keyword: string | null;
  createdAt: string;
  publishedExternallyAt: string | null;
  externalPostUrl: string | null;
  externalPushAttempts: number;
  externalPushLastError: string | null;
}

export const externalApi = {
  create: (body: {
    domain: string; niche: string; city: string; state: string;
    defaultProbeLocation?: string; publishUrl?: string; publishSecret?: string;
  }) => api<{ ok: boolean; siteId?: string; domain?: string; error?: string }>(
    `/api/dashboard/external/sites`, { method: "POST", body: JSON.stringify(body) },
  ),
  updateEndpoint: (siteId: string, body: { publishUrl?: string; publishSecret?: string; defaultProbeLocation?: string }) =>
    api<{ ok: boolean }>(`/api/dashboard/external/sites/${encodeURIComponent(siteId)}`, {
      method: "PATCH", body: JSON.stringify(body),
    }),
  keywords: {
    list: (siteId: string) =>
      api<{ keywords: ExternalKeyword[] }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-keywords`),
    add: (siteId: string, body: { keyword: string; locationName?: string; tag?: string }) =>
      api<{ ok: boolean }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-keywords`, {
        method: "POST", body: JSON.stringify(body),
      }),
    remove: (siteId: string, keywordId: string) =>
      api<{ ok: boolean }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-keywords/${encodeURIComponent(keywordId)}`, {
        method: "DELETE",
      }),
  },
  competitors: {
    list: (siteId: string) =>
      api<{ competitors: ExternalCompetitor[] }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-competitors`),
    add: (siteId: string, body: { competitorDomain: string; notes?: string }) =>
      api<{ ok: boolean }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-competitors`, {
        method: "POST", body: JSON.stringify(body),
      }),
    remove: (siteId: string, competitorId: string) =>
      api<{ ok: boolean }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-competitors/${encodeURIComponent(competitorId)}`, {
        method: "DELETE",
      }),
  },
  testPush: (siteId: string) =>
    api<{ ok: boolean; status?: number; url?: string; error?: string; rawBody?: string }>(
      `/api/dashboard/sites/${encodeURIComponent(siteId)}/external-test-push`, { method: "POST" },
    ),
  pushHistory: (siteId: string) =>
    api<{ posts: ExternalPushHistoryItem[] }>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/external-push-history`),
};

// ─── Cross-portfolio optimization target list ──────────────────────────

export interface OptimizeTarget {
  siteId: string;
  domain: string;
  keyword: string;
  ourPosition: number;
  localPack: boolean;
  directorySlots: number;
  chainSlots: number;
  localSlots: number;
  ease: number;
  probedAt: string;
}
export interface OptimizeTargetsResponse {
  summary: {
    total: number;
    in1130: number;
    in3150: number;
    sites: number;
  };
  targets: OptimizeTarget[];
}
export interface OptimizationAttempt {
  id: string;
  siteId?: string;
  domain?: string | null;
  keyword: string;
  interventionKind: string;
  gradeScore: number | null;
  gradeLetter: string | null;
  prePosition: number | null;
  preWordCount: number | null;
  attemptedAt: string;
  ageDays?: number;
  threadKey: string | null;
  githubRunUrl: string | null;
  /** Page URL Owen targeted (today always the homepage; future per-page
   *  briefs may target /services/<slug>.html or /blog/<slug>.html). */
  targetUrl: string | null;
  postPositionD7: number | null;
  postPositionD14: number | null;
  postPositionD28: number | null;
  latestPosition: number | null;
  latestProbedAt: string | null;
  delta7?: number | null;
  delta14?: number | null;
  deltaLatest?: number | null;
  movement?: "awaiting" | "top10_hit" | "improving" | "flat" | "regressing" | "superseded";
  /** False when this attempt was overwritten by a later regen on the
   *  same (site, keyword). Superseded attempts don't affect the live
   *  homepage, so KPIs only count isCurrent=true rows. */
  isCurrent?: boolean;
}
export interface OptimizeImpactResponse {
  kpis: {
    total: number;
    top10Hits: number;
    improving: number;
    regressing: number;
    flat: number;
    awaiting: number;
    supersededCount: number;
    gradeBuckets: { A: number; B: number; C: number; D: number; F: number; ungraded: number };
    avgDelta14: number | null;
    avgDeltaLatest: number | null;
  };
  topImprovers: OptimizationAttempt[];
  topRegressors: OptimizationAttempt[];
  attempts: OptimizationAttempt[];
}
export interface SiteAttemptsResponse {
  attempts: OptimizationAttempt[];
}
export interface DiscoveredKeyword {
  id: string;
  keyword: string;
  sourceCompetitorDomain: string;
  estVolume: number | null;
  estDifficulty: number | null;
  estCompetitorPosition: number | null;
  status: "pending" | "probed" | "skipped";
  discoveredAt: string;
  probedAt: string | null;
  latestPosition: number | null;
  /** For pending keywords: portfolio-wide queue rank (1-based) in
   *  Spencer's volume-DESC fast-drain order. NULL for non-pending. */
  queuePosition: number | null;
  /** Total pending keywords portfolio-wide — denominator of position. */
  queueTotal: number | null;
  /** ISO timestamp when the page cooldown for this keyword's target
   *  expires. NULL means no cooldown active. v1: target = homepage. */
  pageCooldownUntil: string | null;
}
export interface DiscoveredKeywordsResponse {
  counts: { total: number; pending: number; probed: number; bySource: Record<string, number> };
  keywords: DiscoveredKeyword[];
}
export interface ActiveCooldown {
  siteId: string;
  targetUrl: string;
  lastAttemptAt: string;
  expiresAt: string;
}
export interface ActiveCooldownsResponse { cooldowns: ActiveCooldown[]; }

export interface NextRetryEtaResponse {
  doris: { nextRunAt: string | null; intervalSec: number | null; enabled: boolean | null } | null;
}

export interface MomentumBucket {
  total_pairs?: number;
  in_top_3?: number;
  in_top_10?: number;
  in_push_zone?: number;
  deep?: number;
  not_ranked?: number;
  avg_rank?: number | null;
}
export interface MomentumCrossover {
  site_id: string;
  domain: string;
  keyword: string;
  from_pos: number | null;
  to_pos: number | null;
}
export interface MomentumResponse {
  asOf: string;
  history: { earliest: string | null; depthDays: number };
  current: {
    totalPairs: number;
    inTop3: number;
    inTop10: number;
    inPushZone: number;
    deep: number;
    notRanked: number;
    inLocalPack: number;
    avgRank: number | null;
    competingSites: number;
  };
  past: { d7: MomentumBucket; d14: MomentumBucket; d28: MomentumBucket };
  movers: {
    best: Array<{ siteId: string; domain: string; pairs: number; avgDelta: number | null; improved: number; regressed: number }>;
    worst: Array<{ siteId: string; domain: string; pairs: number; avgDelta: number | null; improved: number; regressed: number }>;
  };
  crossovers: {
    enteredTop3: MomentumCrossover[];
    enteredTop10: MomentumCrossover[];
    enteredPushZone: MomentumCrossover[];
    fellOutTop10: MomentumCrossover[];
    fellOutTop30: MomentumCrossover[];
  };
  owenAttribution: {
    totalAttempts: number;
    moved3plus: number;
    avgPre: number | null;
    avgPost14: number | null;
    medianDelta14: number | null;
  };
}
export const momentumApi = {
  read: () => api<MomentumResponse>("/api/dashboard/momentum"),
};

export const optimizeApi = {
  targets: () => api<OptimizeTargetsResponse>("/api/dashboard/optimize/targets"),
  impact: () => api<OptimizeImpactResponse>("/api/dashboard/optimize/impact"),
  siteAttempts: (siteId: string) => api<SiteAttemptsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/optimize-attempts`),
  siteDiscovered: (siteId: string) => api<DiscoveredKeywordsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/discovered-keywords`),
  activeCooldowns: () => api<ActiveCooldownsResponse>("/api/dashboard/optimize/active-cooldowns"),
  nextRetryEta: () => api<NextRetryEtaResponse>("/api/dashboard/agents/next-retry-eta"),
};

export const outreachApi = {
  list: (siteId?: string) =>
    api<OutreachResponse>(`/api/dashboard/outreach${siteId ? "?siteId=" + encodeURIComponent(siteId) : ""}`),
  create: (input: OutreachInput) =>
    api<{ ok: boolean; id: string }>("/api/dashboard/outreach", { method: "POST", body: JSON.stringify(input) }),
  update: (id: string, patch: Partial<{ status: string; cost: number; linkUrl: string; notes: string }>) =>
    api<{ ok: boolean }>(`/api/dashboard/outreach/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),
};

export const clientsApi = {
  list: () => api<ClientsResponse>("/api/dashboard/clients"),
  create: (input: ClientInput) => api<{ ok: boolean; id: string }>(
    "/api/dashboard/clients",
    { method: "POST", body: JSON.stringify(input) },
  ),
  update: (id: string, input: Partial<ClientInput>) => api<{ ok: boolean }>(
    `/api/dashboard/clients/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  ),
};

export const rentalsApi = {
  list: () => api<RentalsResponse>("/api/dashboard/rentals"),
  create: (input: RentalInput) => api<{ ok: boolean; id: string }>(
    "/api/dashboard/rentals",
    { method: "POST", body: JSON.stringify(input) },
  ),
  update: (id: string, input: Partial<{ status: string; monthlyRate: number; endDate: string; paymentMethod: string; notes: string }>) =>
    api<{ ok: boolean }>(
      `/api/dashboard/rentals/${encodeURIComponent(id)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    ),
};

export const leadsApi = {
  list: (params: { siteId?: string; minScore?: number; cursor?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.siteId) q.set("siteId", params.siteId);
    if (params.minScore !== undefined) q.set("minScore", String(params.minScore));
    if (params.cursor) q.set("cursor", params.cursor);
    const qs = q.toString();
    return api<LeadsResponse>(`/api/dashboard/leads${qs ? "?" + qs : ""}`);
  },
};

// ─── Monitoring (alerts inbox, health, sitemap report) ─────────────────

export interface AlertExecutable {
  kind: "blog-render" | "optimize" | "generate-refresh" | "retry-publish-signals";
  domain?: string;
  siteId?: string;
  briefId?: string;
  keyword?: string;
}
export interface Alert {
  id: string;
  siteId: string | null;
  domain: string | null;
  raisedAt: string;
  severity: "critical" | "warn" | "info";
  category: string;
  title: string;
  description: string | null;
  dedupKey: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  executable: AlertExecutable | null;
  executionUrl: string | null;
  executedAt: string | null;
  /** Inbox thread key — alerts sharing this render as one Gmail-style
   *  thread in the InboxPage. Null = standalone. */
  threadKey: string | null;
}

export interface AlertsResponse {
  counts: { critical: number; warn: number; info: number; total: number };
  alerts: Alert[];
}

export interface HealthCheck {
  id: string;
  category: string;
  checkName: string;
  status: "pass" | "warn" | "fail";
  detail: unknown;
  remediation: string | null;
  checkedAt: string;
}

export interface HealthResponse {
  overall: "pass" | "warn" | "fail";
  byCategory: Record<string, { pass: number; warn: number; fail: number; worst: string }>;
  checks: HealthCheck[];
}

export interface CommandCenterHealthResponse {
  sites: Array<{
    siteId: string;
    domain: string;
    passCount: number;
    warnCount: number;
    failCount: number;
    stoplight: "pass" | "warn" | "fail" | "unchecked";
  }>;
}

export interface SitemapSubmission {
  id: string;
  engine: "google_sc" | "bing_wmt" | "indexnow";
  feedUrl: string;
  submittedAt: string;
  status: "pending" | "success" | "error";
  lastCrawledAt: string | null;
  urlCount: number | null;
  indexedCount: number | null;
  errors: unknown;
}

export interface SitemapEngineRecovery {
  /** Open publish_signal alert for this engine. */
  hasOpenAlert: boolean;
  /** Real (non-dryrun) Doris attempts on the alert so far. */
  attemptsCount: number;
  /** Most recent attempt time, or null if no attempts. */
  lastAttemptAt: string | null;
  /** Minutes until Doris's next allowed retry per backoff. null when escalated. */
  nextAttemptInMinutes: number | null;
  /** True after MAX_ATTEMPTS exhausted or alert severity == critical. */
  escalated: boolean;
  /** UUID of the open alert, if any. */
  alertId: string | null;
}

export interface SitemapReportResponse {
  latest: Record<"google_sc" | "bing_wmt" | "indexnow", SitemapSubmission | null>;
  history: SitemapSubmission[];
  /** Per-engine Doris recovery status. Only present for engines that
   *  have a publish_signal channel mapped (google_sc → sc_sitemap, etc). */
  recovery: Record<"google_sc" | "bing_wmt" | "indexnow", SitemapEngineRecovery>;
}

export const alertsApi = {
  list: (opts: { includeResolved?: boolean; siteId?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.includeResolved) q.set("include", "resolved");
    if (opts.siteId) q.set("site_id", opts.siteId);
    const qs = q.toString();
    return api<AlertsResponse>(`/api/dashboard/alerts${qs ? "?" + qs : ""}`);
  },
  ack: (id: string) =>
    api<{ ok: boolean }>(`/api/dashboard/alerts/${encodeURIComponent(id)}/ack`, { method: "POST" }),
  resolve: (id: string, note?: string) =>
    api<{ ok: boolean }>(`/api/dashboard/alerts/${encodeURIComponent(id)}/resolve`, {
      method: "POST",
      body: JSON.stringify(note ? { note } : {}),
    }),
  execute: (id: string) =>
    api<{ ok: boolean; executionUrl?: string; alreadyDispatched?: boolean; error?: string }>(
      `/api/dashboard/alerts/${encodeURIComponent(id)}/execute`,
      { method: "POST" },
    ),
  reRun: (id: string) =>
    api<{ ok: boolean; runsUrl?: string; domain?: string; keyword?: string; error?: string }>(
      `/api/dashboard/alerts/${encodeURIComponent(id)}/re-run`,
      { method: "POST" },
    ),
};

export const healthApi = {
  forSite: (siteId: string) =>
    api<HealthResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/health`),
  commandCenter: () =>
    api<CommandCenterHealthResponse>(`/api/dashboard/command-center/health`),
};

export interface AhrefsAuditIssueRow {
  issueType: string;
  issueTitle: string | null;
  severity: "error" | "warning" | "notice";
  /** Post-CF-false-positive-filter count (Ahrefs UI raw counts include
   *  cdn-cgi email-protection URLs we strip). */
  realCount: number;
  sampleUrls: string[] | null;
  firstSeen: string | null;
  lastSeen: string | null;
}
export interface AhrefsIssuesResponse {
  ok: boolean;
  issues: AhrefsAuditIssueRow[];
}

export interface IntegrationStatus {
  ok: boolean;
  integration: string;
  status: "disconnected" | "pending" | "connected" | "expired";
  browserbaseContextId: string | null;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
  metadata: Record<string, unknown> | null;
  configured: boolean;
  /** ahrefs-only: how many sites have an ahrefs_project_id (recrawl targets). */
  mappedProjectCount?: number;
}

export interface ConnectStartResponse {
  ok: boolean;
  liveViewUrl: string;
  sessionId: string;
  contextId: string;
  targetUrl: string | null;
}

export interface AhrefsProject {
  siteId: string;
  domain: string;
  ahrefsProjectId: string | null;
}

export const integrationsApi = {
  status: (name: string) =>
    api<IntegrationStatus>(`/api/dashboard/integrations/${encodeURIComponent(name)}/status`),
  connectStart: (name: string) =>
    api<ConnectStartResponse>(`/api/dashboard/integrations/${encodeURIComponent(name)}/connect-start`, {
      method: "POST",
    }),
  connectConfirm: (name: string) =>
    api<{ ok: boolean; status: string }>(
      `/api/dashboard/integrations/${encodeURIComponent(name)}/connect-confirm`,
      { method: "POST" },
    ),
  disconnect: (name: string) =>
    api<{ ok: boolean; status: string }>(
      `/api/dashboard/integrations/${encodeURIComponent(name)}/disconnect`,
      { method: "POST" },
    ),
  ahrefsProjects: () =>
    api<{ ok: boolean; projects: AhrefsProject[] }>(`/api/dashboard/integrations/ahrefs/projects`),
  ahrefsRecrawl: (body: { all?: boolean; projectIds?: string[] }) =>
    api<{ ok: boolean; dispatched: number; runsUrl: string }>(
      `/api/dashboard/integrations/ahrefs/recrawl`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  ahrefsExcludeUrls: (body: { all?: boolean; projectIds?: string[]; pattern?: string }) =>
    api<{ ok: boolean; dispatched: number; pattern: string; runsUrl: string }>(
      `/api/dashboard/integrations/ahrefs/exclude-urls`,
      { method: "POST", body: JSON.stringify(body) },
    ),
};

export const sitemapApi = {
  forSite: (siteId: string) =>
    api<SitemapReportResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/sitemap-report`),
};

export interface AhrefsSnapshot {
  snapshotAt: string;
  domainRating: number | null;
  backlinksCount: number | null;
  refDomainsCount: number | null;
  orgTrafficEstimate: number | null;
  orgTrafficValueCents?: number | null;
  topAnchors?: unknown;
  spamBacklinkCount?: number | null;
  spamBacklinkSampleSize?: number | null;
  spamVerdict?: "clean" | "mild" | "spammy" | string | null;
  spammyRoots?: string[] | null;
}
export interface AhrefsTrendPoint {
  snapshotAt: string;
  domainRating: number | null;
  backlinksCount: number | null;
  refDomainsCount: number | null;
}
export interface AhrefsResponse {
  latest: AhrefsSnapshot | null;
  weekAgo: AhrefsSnapshot | null;
  trend90d: AhrefsTrendPoint[];
}
export const ahrefsApi = {
  forSite: (siteId: string) =>
    api<AhrefsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/ahrefs`),
};

// ─── PageSpeed Insights (CWV + Lighthouse) ────────────────────────────

export type PsiCwvBucket = "good" | "needs-improvement" | "poor" | "no-data";

export interface PsiRun {
  url: string;
  strategy: "mobile" | "desktop";
  lighthouse: {
    performance: number | null;
    accessibility: number | null;
    bestPractices: number | null;
    seo: number | null;
  };
  crux: {
    hasData: boolean;
    lcpMs: number | null;
    inpMs: number | null;
    cls: number | null;
    ttfbMs: number | null;
    fcpMs: number | null;
    overall: PsiCwvBucket | null;
  };
  checkedAt: string;
}

export interface PsiResponse {
  dataRefreshedAt: string | null;
  byStrategy: {
    mobile: { good: number; needsImprovement: number; poor: number; noData: number };
    desktop: { good: number; needsImprovement: number; poor: number; noData: number };
  };
  runs: PsiRun[];
}

export const psiApi = {
  forSite: (siteId: string) =>
    api<PsiResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/psi`),
};

// ─── Phase B6: rank tracker ────────────────────────────────────────────

export interface RankRow {
  keyword: string;
  position: number | null;
  localPack: boolean;
  localPackPosition: number | null;
  /** Monthly search volume from DataForSEO keyword_overview (US-wide).
   *  null when DataForSEO has no data, or when this row predates the
   *  2026-05-09 schema add. Render "—" in the dashboard when null. */
  searchVolume: number | null;
}
export interface RankingsResponse {
  days: number;
  dataRefreshedAt: string | null;
  latest: RankRow[];
  trends: Record<string, Array<{ day: string; position: number | null }>>;
}

export interface RankPullRefreshOk {
  ok: true;
  keywordsChecked: number;
  keywordsRanked: number;
  checkedAt: string;
}
export interface RankPullRefreshCooldown {
  ok: false;
  error: "cooldown";
  lastPulledAt: string;
  retryAfterSec: number;
  cooldownHours: number;
}

export const rankingsApi = {
  forSite: (siteId: string, days = 60) =>
    api<RankingsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/rankings?days=${days}`),
  refresh: (siteId: string) =>
    api<RankPullRefreshOk>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/rank-pull-refresh`, {
      method: "POST",
    }),
};

// ─── Phase Twilio: per-site call attribution ───────────────────────────

export interface CallTracking {
  phoneNumber: string | null;
  phoneForwardTo: string | null;
  provider: string | null;
  provisionedAt: string | null;
  callMode?: string | null;
  voicemailGreeting?: string | null;
}
export interface CallSummary {
  calls: number;
  smses: number;
  voicemails: number;
  completedCalls: number;
  missedCalls: number;
  avgDurationSec: number | null;
}
export interface CallEntry {
  id: string;
  type: "call" | "sms" | "voicemail";
  callerPhone: string | null;
  message: string | null;
  durationSeconds: number | null;
  qualityScore: number | null;
  receivedAt: string;
  /** Voicemail-only: Twilio's RecordingSid; the dashboard's audio
   *  player hits /webhooks/twilio/recording-proxy?sid=X to stream it. */
  recordingSid?: string | null;
  /** Voicemail-only: Twilio's async-populated transcript text. NULL
   *  until the transcribe-callback fires (~30-60s after recording). */
  transcript?: string | null;
}
export interface CallsResponse {
  days: number;
  tracking: CallTracking | null;
  summary: CallSummary;
  recent: CallEntry[];
}

export const callsApi = {
  forSite: (siteId: string, days = 28) =>
    api<CallsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/calls?days=${days}`),
};

// ─── /ops/twilio — portfolio-wide phones panel (#82) ───────────────────

export interface TwilioAccountInfo {
  sid: string | null;
  type: string | null;
  status: string | null;
  friendlyName: string | null;
  balanceUsd: number | null;
  currency: string | null;
}

export interface TwilioUsageCategory {
  category: string;
  description: string;
  priceUsd: number;
  count: number;
  countUnit: string;
}

export interface TwilioInventory {
  totalSites: number;
  provisioned: number;
  unprovisioned: number;
  byMode: { voicemail: number; forward: number; hybrid: number };
  byProvider: { twilio: number; signalwire: number };
  monthlyRentalCents: number;
  recurring: Array<{ provider: string; description: string; monthlyCostCents: number }>;
}

export interface TwilioSiteRow {
  domain: string;
  phoneNumber: string | null;
  provider: string | null;
  callMode: string | null;
  provisionedAt: string | null;
  recentCalls: number;
  recentVoicemails: number;
  recentSmses: number;
}

export interface TwilioRecentEntry {
  id: string;
  type: "call" | "voicemail" | "sms";
  siteId: string;
  siteDomain: string;
  callerPhone: string | null;
  message: string | null;
  durationSeconds: number | null;
  receivedAt: string;
}

export interface TwilioOpsResponse {
  account: TwilioAccountInfo | null;
  usage: { windowDays: number; totalUsd: number; categories: TwilioUsageCategory[] };
  inventory: TwilioInventory;
  sites: TwilioSiteRow[];
  recent: TwilioRecentEntry[];
  webhookHealth: { activityLast24h: number; activityLast7d: number };
  showTests?: boolean;
  includeArchived?: boolean;
  dataIntegrity?: DataIntegrity;
}

export const twilioOpsApi = {
  status: () => api<TwilioOpsResponse>("/api/dashboard/ops/twilio"),
};

// ─── Phase B4: GSC refresh queue ───────────────────────────────────────

export interface RefreshQueueItem {
  keyword: string;
  impressions: number;
  clicks: number;
  avgPosition: number;
}
export interface RefreshQueueResponse {
  minImpressions: number;
  days: number;
  items: RefreshQueueItem[];
}

export const refreshQueueApi = {
  forSite: (siteId: string, minImpressions = 20) =>
    api<RefreshQueueResponse>(
      `/api/dashboard/sites/${encodeURIComponent(siteId)}/refresh-queue?min_impressions=${minImpressions}`,
    ),
};

// ─── Phase B1: citations tracker ───────────────────────────────────────

export interface Citation {
  id: string;
  platform: string;
  tier: number;
  status: "queued" | "submitted" | "live" | "rejected" | "needs_review";
  listingUrl: string | null;
  submittedAt: string | null;
  verifiedLiveAt: string | null;
  notes: string | null;
  packet: unknown;
}
export interface CitationsResponse {
  citations: Citation[];
}

export interface CitationsPortfolioSite {
  siteId: string;
  domain: string;
  niche: string | null;
  city: string | null;
  state: string | null;
  queued: number;
  submitted: number;
  live: number;
  declined: number;
  failed: number;
  total: number;
  tier1Total: number;
  tier1Done: number;
  top30Kws: number;
}

export interface CitationsPortfolioResponse {
  summary: { queued: number; submitted: number; live: number; total: number };
  sites: CitationsPortfolioSite[];
}

export interface CitationsNextAction {
  citationId: string;
  siteId: string;
  domain: string;
  niche: string | null;
  platform: string;
  tier: number;
  top30Kws: number;
  leverage: number;
}

export const citationsApi = {
  forSite: (siteId: string) =>
    api<CitationsResponse>(`/api/dashboard/sites/${encodeURIComponent(siteId)}/citations`),
  portfolio: () =>
    api<CitationsPortfolioResponse>(`/api/dashboard/citations/portfolio`),
  nextActions: (limit = 10) =>
    api<{ actions: CitationsNextAction[] }>(`/api/dashboard/citations/next-actions?limit=${limit}`),
  update: (
    id: string,
    patch: Partial<{
      status: string;
      listingUrl: string;
      notes: string;
      markSubmitted: boolean;
      markLive: boolean;
    }>,
  ) =>
    api<{ ok: boolean }>(`/api/dashboard/citations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
};

export interface OperatorListing {
  id: string;
  platform: string;
  name: string;
  tier: number;
  status: "queued" | "submitted" | "live" | string;
  submitUrl: string | null;
  listingUrl: string | null;
  notes: string | null;
  submittedAt: string | null;
  verifiedLiveAt: string | null;
}
export interface OperatorListingsResponse { listings: OperatorListing[]; }
export const operatorListingsApi = {
  list: () => api<OperatorListingsResponse>("/api/dashboard/operator/listings"),
  update: (
    id: string,
    patch: Partial<{
      status: string;
      listingUrl: string;
      notes: string;
      markSubmitted: boolean;
      markLive: boolean;
    }>,
  ) =>
    api<{ ok: boolean }>(`/api/dashboard/operator/listings/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
};

// ─── Goldie: prospect domain queue (#96) ───────────────────────────────

export interface ProspectRow {
  id: string;
  domain: string;
  source: string;
  status: "queued" | "approved" | "registered" | "declined" | "failed" | "skipped" | string;
  score: number | null;
  ahrefsDr: number | null;
  ahrefsBacklinks: number | null;
  spamVerdict: "clean" | "mild" | "spammy" | "unknown" | string | null;
  spamCount: number | null;
  spamSampleSize: number | null;
  nicheMatch: string | null;
  cityMatch: string | null;
  stateMatch: string | null;
  statusReason: string | null;
  discoveredAt: string;
  resolvedAt: string | null;
  registeredSiteId: string | null;
}

export interface ProspectsResponse {
  prospects: ProspectRow[];
  summary: Record<string, number>;
  spend30dCents: number;
}

export const prospectsApi = {
  list: (limit = 100) =>
    api<ProspectsResponse>(`/api/dashboard/ops/prospects?limit=${limit}`),
  approve: (id: string) =>
    api<{ ok: boolean; status: string }>(
      `/api/dashboard/ops/prospects/${encodeURIComponent(id)}/approve`,
      { method: "POST" },
    ),
  decline: (id: string) =>
    api<{ ok: boolean; status: string }>(
      `/api/dashboard/ops/prospects/${encodeURIComponent(id)}/decline`,
      { method: "POST" },
    ),
};

// ─── Phase B3: testimonials ────────────────────────────────────────────

export interface Testimonial {
  id: string;
  siteId: string | null;
  siteDomain: string | null;
  customerName: string;
  customerCity: string | null;
  serviceSlug: string | null;
  rating: number;
  quote: string;
  sourceUrl: string | null;
  provenanceNotes: string | null;
  status: "draft" | "approved" | "rejected";
  incentivized: boolean;
  ftcDisclosureText: string | null;
  collectedAt: string | null;
  createdAt: string;
  approvedAt: string | null;
}
export interface TestimonialsResponse {
  testimonials: Testimonial[];
}
export interface TestimonialInput {
  siteId?: string | null;
  customerName: string;
  customerCity?: string;
  serviceSlug?: string;
  rating: number;
  quote: string;
  sourceUrl?: string;
  provenanceNotes?: string;
  incentivized?: boolean;
  ftcDisclosureText?: string;
  collectedAt?: string;
}

export const testimonialsApi = {
  list: (siteId?: string, status?: string) => {
    const q = new URLSearchParams();
    if (siteId) q.set("siteId", siteId);
    if (status) q.set("status", status);
    const qs = q.toString();
    return api<TestimonialsResponse>(`/api/dashboard/testimonials${qs ? "?" + qs : ""}`);
  },
  create: (input: TestimonialInput) =>
    api<{ ok: boolean; id: string }>("/api/dashboard/testimonials", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  update: (id: string, patch: Partial<{ status: string; quote: string; rating: number; provenanceNotes: string }>) =>
    api<{ ok: boolean }>(`/api/dashboard/testimonials/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  remove: (id: string) =>
    api<{ ok: boolean }>(`/api/dashboard/testimonials/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
};

// ─── Connie the Compliance — generator-violations dashboard ─────────

export interface ConnieTotals {
  violations: number;
  distinctPatterns: number;
  retrySucceeded: number;
  retryFailedThenSanitized: number;
  sanitizedNoRetry: number;
}
export interface ConniePatternRow {
  patternSource: string;
  reason: string;
  count: number;
  distinctMatches: number;
  lastSeen: string;
  exampleMatches: string[];
}
export interface ConniePageTypeRow { pageType: string; count: number; }
export interface ConnieRecentRow {
  id: string;
  siteId: string | null;
  domain: string | null;
  pageType: string;
  patternSource: string;
  reason: string;
  matchedText: string;
  attemptNumber: number;
  resolution: "retry_succeeded" | "retry_failed_then_sanitized" | "sanitized_no_retry" | string;
  occurredAt: string;
}
export interface ConnieDashboardResponse {
  windowDays: number;
  totals: ConnieTotals;
  byPattern: ConniePatternRow[];
  byPageType: ConniePageTypeRow[];
  recent: ConnieRecentRow[];
}

export const connieApi = {
  dashboard: (params: { days?: number; recentLimit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.days != null) qs.set("days", String(params.days));
    if (params.recentLimit != null) qs.set("recentLimit", String(params.recentLimit));
    return api<ConnieDashboardResponse>(`/api/dashboard/connie/dashboard${qs.size ? "?" + qs.toString() : ""}`);
  },
};

